
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Profile } from "@/hooks/useProfile";

export const useProfileHandlers = (profile: Profile | null, refetch: () => void) => {
  const [avatarUploading, setAvatarUploading] = useState(false);

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setAvatarUploading(true);
      
      // Gate behind session check
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || !profile?.user_id) {
        throw new Error('No authenticated session found');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `${profile.user_id}.${fileExt}`;
      
      // Get proper MIME type for the avatar image
      const mimeType = file.type || `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, { 
          upsert: true,
          contentType: mimeType
        });

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: data.publicUrl })
        .eq('user_id', profile.user_id);

      if (updateError) throw updateError;

      toast.success("Avatar updated successfully!");
      refetch();
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error("Failed to upload avatar");
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleTimelapseClick = (crop: string, setSelectedCropForTimelapse: (crop: string) => void, setActiveTab: (tab: string) => void) => {
    setSelectedCropForTimelapse(crop);
    setActiveTab('posts');
  };

  const handlePostClick = (post: any, setSelectedPostForPopup: (post: any) => void) => {
    setSelectedPostForPopup(post);
  };

  const handleRemoveCrop = (cropId: string, cropName: string, removeCropFromPlan: (cropId: string) => void) => {
    removeCropFromPlan(cropId);
  };

  return {
    avatarUploading,
    handleAvatarUpload,
    handleTimelapseClick,
    handlePostClick,
    handleRemoveCrop
  };
};
